/**
 * CF kind resolver for simulation v2 (spec §6).
 *
 * Looks up a CF's kind from the manifest; falls back to name heuristics
 * when the manifest entry is missing or the kind field is absent.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import type { CfKind } from './simulator.js';

/** Display labels for each CF kind. */
export const CF_KIND_LABELS: Record<CfKind, string> = {
  trigger: 'TRIGGER',
  callable: 'CALLABLE',
  scheduled: 'SCHEDULED',
  http: 'HTTP',
};

/**
 * Heuristic CF kind resolution from a CF name, used when manifest data is absent.
 * Priority: 'scheduled' → 'callable' → 'http' → 'trigger' (default)
 */
function heuristicKind(cfName: string): CfKind {
  const lower = cfName.toLowerCase();
  if (lower.includes('schedule') || lower.includes('cron') || lower.startsWith('reset')) {
    return 'scheduled';
  }
  if (lower.includes('callable') || lower.startsWith('call')) return 'callable';
  if (lower.includes('http') || lower.startsWith('api')) return 'http';
  return 'trigger';
}

/**
 * Returns the CfKind for a given CF name.
 * Checks manifest.cloudFunctions first; falls back to heuristic.
 * Returns null if cfName is empty/undefined.
 */
export function getCfKind(cfName: string | undefined, manifest: AtlasManifest): CfKind | null {
  if (!cfName) return null;
  const cf = manifest.cloudFunctions.find((c) => c.name === cfName);
  if (cf?.kind) return cf.kind as CfKind;
  return heuristicKind(cfName);
}

/**
 * Returns the actor lane for a CF kind.
 * 'callable' → 'cf-callable'; all others → 'cf'.
 */
export function cfKindToActor(kind: CfKind): 'cf' | 'cf-callable' {
  return kind === 'callable' ? 'cf-callable' : 'cf';
}
